import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const imageKeys = ['company_logo', 'profile_picture', 'banner_picture', 'signature'];

const PillsList = ({ items }) => (
    <div className="flex flex-wrap gap-1 justify-end">
        {items.map((item, i) => (
            <span key={i} className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-medium">
                {item}
            </span>
        ))}
    </div>
);

const parseArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
        try {
            const parsed = JSON.parse(val);
            return Array.isArray(parsed) ? parsed : null;
        } catch { return null; }
    }
    return null;
};

const FieldRow = ({ label, value }) => {
    const isImage = imageKeys.includes(label) || (typeof value === 'string' && /^https?:\/\/.*\.(jpg|jpeg|png|gif|webp|svg)/i.test(value));
    const arr = parseArray(value);

    return (
        <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0 items-start">
            <span className="text-xs font-medium text-gray-500 capitalize">{label.replace(/_/g, ' ')}</span>
            {arr ? (
                <PillsList items={arr} />
            ) : isImage && value ? (
                <div className="relative">
                    <img
                        src={value}
                        alt={label}
                        className="max-w-[100px] max-h-[60px] rounded object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            ) : (
                <span className="text-xs text-gray-900 text-right max-w-[60%] break-words">{value ?? '-'}</span>
            )}
        </div>
    );
};

const UserDetailModal = ({ table, userId, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await api.get(`${API_CONFIG.ADMIN.USER_DETAILS}/${table}/${userId}/details`);
                if (res.data.status === 'success') {
                    setData(res.data);
                }
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to fetch user details');
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [table, userId]);

    const ignoreKeys = ['password', 'reset_token', 'reset_token_created_at', 'verification_code', 'transaction_pin', 'payment_pin'];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-bold text-gray-900">
                        {data?.user?.fullname || data?.user?.username || 'User Details'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {loading && (
                    <div className="p-12 text-center text-gray-500">Loading user details...</div>
                )}

                {error && (
                    <div className="m-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">{error}</div>
                )}

                {data && (
                    <div className="p-6 space-y-8">
                        <section>
                            <h3 className="text-base font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">User Profile</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
                                {Object.entries(data.user)
                                    .filter(([k]) => !ignoreKeys.includes(k))
                                    .map(([key, val]) => (
                                        <FieldRow key={key} label={key} value={val?.toString()} />
                                    ))}
                            </div>
                        </section>

                        <section>
                            <h3 className="text-base font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                                Payment Accounts ({data.payment_accounts?.length || 0})
                            </h3>
                            {!data.payment_accounts?.length ? (
                                <p className="text-sm text-gray-500 italic">No payment accounts found.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                        <thead>
                                            <tr className="bg-gray-50">
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">ID</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Bank</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Account No</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Account Name</th>
                                                <th className="px-3 py-2 text-right font-medium text-gray-500">Balance</th>
                                                <th className="px-3 py-2 text-right font-medium text-gray-500">Ledger</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Currency</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Pre-Paymeo</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Created</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {data.payment_accounts.map(pa => (
                                                <tr key={pa.id} className="hover:bg-gray-50">
                                                    <td className="px-3 py-2 text-gray-900">{pa.id}</td>
                                                    <td className="px-3 py-2 text-gray-600">{pa.bank_name || '-'}</td>
                                                    <td className="px-3 py-2 text-gray-600 font-mono">{pa.account_number || '-'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{pa.account_name || '-'}</td>
                                                    <td className="px-3 py-2 text-right text-gray-900 font-medium">{pa.balance ?? '-'}</td>
                                                    <td className="px-3 py-2 text-right text-gray-600">{pa.ledger_balance ?? '-'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{pa.currency || '-'}</td>
                                                    <td className="px-3 py-2">
                                                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${pa.is_pre_paymeo ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                            {pa.is_pre_paymeo ? 'Yes' : 'No'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-500">{pa.date_created ? new Date(pa.date_created).toLocaleDateString() : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section>
                            <h3 className="text-base font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                                Bank Accounts ({data.bank_accounts?.length || 0})
                            </h3>
                            {!data.bank_accounts?.length ? (
                                <p className="text-sm text-gray-500 italic">No bank accounts found.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-xs">
                                        <thead>
                                            <tr className="bg-gray-50">
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">ID</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Bank</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Account No</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Account Name</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Default</th>
                                                <th className="px-3 py-2 text-left font-medium text-gray-500">Created</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {data.bank_accounts.map(ba => (
                                                <tr key={ba.id} className="hover:bg-gray-50">
                                                    <td className="px-3 py-2 text-gray-900">{ba.id}</td>
                                                    <td className="px-3 py-2 text-gray-600">{ba.bank_name || '-'}</td>
                                                    <td className="px-3 py-2 text-gray-600 font-mono">{ba.account_number || '-'}</td>
                                                    <td className="px-3 py-2 text-gray-600">{ba.account_name || '-'}</td>
                                                    <td className="px-3 py-2">
                                                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${ba.is_default ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                                                            {ba.is_default ? 'Yes' : 'No'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-500">{ba.created_at ? new Date(ba.created_at).toLocaleDateString() : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserDetailModal;